import React, { useState } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import 'react-day-picker/dist/style.css';
import './DatePicker.css';

interface DatePickerProps {
  value: { from?: Date; to?: Date };
  onChange: (range: { from?: Date; to?: Date }) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Select date range",
  label,
  className = "",
  minDate,
  maxDate
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (range: DateRange | undefined) => {
    if (range) {
      // Normalize dates to noon to avoid timezone edge cases
      // This prevents dates from shifting when crossing midnight UTC
      const normalizeToNoon = (date: Date | undefined): Date | undefined => {
        if (!date) return undefined;
        const normalized = new Date(date);
        normalized.setHours(12, 0, 0, 0);
        return normalized;
      };
      onChange({
        from: normalizeToNoon(range.from),
        to: normalizeToNoon(range.to)
      });
    } else {
      onChange({ from: undefined, to: undefined });
    }
  };

  const formatDisplayDate = (): string => {
    if (!value.from) return placeholder;

    if (!value.to) {
      return format(value.from, 'MMM dd, yyyy');
    }

    return `${format(value.from, 'MMM dd, yyyy')} - ${format(value.to, 'MMM dd, yyyy')}`;
  };

  return (
    <div className={`relative w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {/* Custom styled button that opens the date picker */}
      <div className="relative w-full">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="input-field w-full flex items-center justify-between text-left cursor-pointer"
        >
          <span className={value.from ? 'text-gray-900' : 'text-gray-500'}>
            {formatDisplayDate()}
          </span>

          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>

        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />

            {/* Date picker dropdown */}
            <div className="fixed md:absolute left-1/2 md:left-0 top-1/2 md:top-auto -translate-x-1/2 md:translate-x-0 -translate-y-1/2 md:translate-y-0 md:mt-2 z-20 bg-white border border-gray-200 rounded-xl shadow-xl p-3 md:p-4 w-[90vw] max-w-sm md:w-auto md:max-w-none">
              <DayPicker
                mode="range"
                selected={value as DateRange}
                onSelect={handleSelect}
                disabled={minDate ? { before: minDate } : undefined}
                fromDate={minDate}
                toDate={maxDate}
                numberOfMonths={1}
              />

              {/* Action buttons */}
              <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    onChange({ from: undefined, to: undefined });
                    setIsOpen(false);
                  }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Helper text */}
      <p className="mt-1 text-xs text-gray-500">
        Click to select a date range
      </p>
    </div>
  );
}

// Simple date formatting utilities
export const dateUtils = {
  formatForDisplay: (dateString: string): string => {
    if (!dateString) return '';
    try {
      // Handle YYYY-MM-DD format directly to avoid timezone issues
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
      
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  },

  formatForAPI: (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return dateString;
    }
  },

  isValidDate: (dateString: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }
};